import * as Scrivito from "scrivito";
import React, { useCallback, useMemo } from "react";
import NiceModal from "@ebay/nice-modal-react";
import cx from "classnames";

import { ProjectInfo } from "widgets/RecentProjectsWidget/shared/ProjectInfo";
import { Button, Icon, TranslateWrapper } from "components";
import { ProjectInfoSkeleton } from "widgets/RecentProjectsWidget/shared/ProjectInfoSkeleton";
import { ProjectListQry } from "api/queries";
import { ProjectDetailsModal } from "components/_Modals/ProjectDetailsModal/ProjectDetailsModal";
import { ProjectCreateMut } from "api/mutations";
import { useTranslate } from "hooks";
import { useNavigateToProject } from "hooks/projectHooks";
import styles from "./RecentProjetcsWidgetComponent.module.scss";
import { useQuery } from "urql";

interface RecentProjectsWidgetProps {
  widget?: Scrivito.Widget;
}

Scrivito.provideComponent(
  "RecentProjectsWidget",
  ({ widget }: RecentProjectsWidgetProps) => {
    if (!widget) return null;
    const translate = useTranslate();
    const navigateToProject = useNavigateToProject();
    const [{ data, fetching, error }] = useQuery({
      query: ProjectListQry,
      variables: {
        limit: 3,
        page: 1,
      },
    });

    const projectListObj = data?.currentUser?.projectList;
    const projects = projectListObj?.items || [];
    const overallStatistics = projectListObj?.overallStatistics;
    const projectsCount =
      overallStatistics &&
      (overallStatistics.notShared ?? 0) + (overallStatistics.shared ?? 0);

    const sideToShow = widget.get("sideToShow") as string;
    const singleProjectText = translate.stringOnly("PROJECT_COUNT_SINGLE");
    const pluralProjectText = translate.stringOnly("PROJECT_COUNT_PLURAL");

    const projectDescription = useMemo(
      () =>
        projectsCount === 1
          ? `${projectsCount} ${singleProjectText}`
          : `${projectsCount || "0"} ${pluralProjectText}`,
      [projectsCount, singleProjectText, pluralProjectText],
    );

    const projectDescriptionWrapped =
      typeof translate("PROJECT_COUNT_PLURAL") === "object" ? (
        <TranslateWrapper
          text={projectDescription}
          tooltipText={
            projectsCount === 1
              ? "PROJECT_COUNT_SINGLE"
              : "PROJECT_COUNT_PLURAL"
          }
        />
      ) : (
        projectDescription
      );

    const { isLeftSideShown, isRightSideShown } = useMemo(() => {
      switch (sideToShow) {
        case "left":
          return { isLeftSideShown: true, isRightSideShown: false };
        case "right":
          return { isLeftSideShown: false, isRightSideShown: true };
        default:
          return { isLeftSideShown: true, isRightSideShown: true };
      }
    }, [sideToShow]);

    const toggleModal = useCallback(() => {
      NiceModal.show(ProjectDetailsModal, {
        mutation: ProjectCreateMut,
      }).then((result) =>
        navigateToProject.configurator((result as { id: string }).id),
      );
    }, [navigateToProject]);

    if (error) {
      return (
        <div className="mb-6">
          <span>{translate("ERROR_OCCURED")}</span>
        </div>
      );
    }

    return (
      <>
        <div className="d-flex flex-column flex-lg-row flex-nowrap w-100 pb-8">
          {isLeftSideShown && (
            <div
              className={cx("d-flex flex-column", {
                "col-lg-6 me-lg-4": isLeftSideShown && isRightSideShown,
                "w-100": isLeftSideShown && !isRightSideShown,
              })}
            >
              <div className="bg-white rounded flex-grow-1 shadow">
                <Scrivito.ContentTag
                  content={widget}
                  widgetProps={{
                    getData: () => projectDescriptionWrapped,
                  }}
                  className={cx("p-0", styles.projectLinkWrapper)}
                  attribute="leftLinks"
                  data-testid="left-links"
                />
                {fetching ? (
                  <div className="px-6 mt-3 mb-1" data-testid="skeletons">
                    <ProjectInfoSkeleton />
                    <ProjectInfoSkeleton />
                    <ProjectInfoSkeleton />
                  </div>
                ) : (
                  <>
                    {projects.length !== 0 && (
                      <div className="px-6 mt-3 mb-1">
                        <h5 className="fs-md">
                          {translate("LATEST_UPDATED_PROJECTS")}
                        </h5>
                        {projects
                          .filter((project) => project !== null)
                          .map((project) => (
                            <ProjectInfo
                              key={project.id}
                              projectId={project.id}
                              editedBy={project.lastEditBy?.fullName ?? ""}
                              {...project}
                            />
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <Button
                data-testid="RecentProjectsWidgetComponent_new-project-btn"
                className="mt-3 w-100 d-flex justify-content-center align-items-center fs-lg"
                onClick={toggleModal}
                size="lg"
                color="primary"
              >
                <Icon iconName="plus-regular" className="icon-16 me-1" />
                {translate("NEW_PROJECT_BUTTON_TEXT")}
              </Button>
            </div>
          )}
          {isRightSideShown && (
            <Scrivito.ContentTag
              content={widget}
              attribute="rightLinks"
              data-testid="right-links"
              className={cx(styles.projectLinkWrapper, {
                "col-lg-6 mt-4 mt-lg-0 d-flex flex-column justify-content-between bg-white rounded shadow":
                  isLeftSideShown && isRightSideShown,
                "w-100": !isLeftSideShown && isRightSideShown,
              })}
            />
          )}
        </div>
      </>
    );
  },
);
